"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { iCustomerPaymentMethod } from "@sdk/customer-payment-methods/customer-payment-method-model";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { SDK } from "@sdk/sdk";
import { useBrands } from "@/hooks/use-brands";
import { CurrencySelector } from "@/components/currency-selector";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { LedgerSelector } from "@/components/ledgers/ledger-selector";
import {
  QuickBooksAccountSelector,
  formatQuickBooksAccountLabel,
} from "@/components/integrations/quickbooks-account-selector";
import { useQuickBooksAccounts } from "@/components/integrations/use-quickbooks-accounts";
import { CustomerPaymentMethodDefaultLedgerLinks } from "@/components/accounting/default-ledger-links";
import { PaymentMethodBrandRestrictionFields } from "@/components/payment-methods/payment-method-brand-restriction-fields";

export interface AddEditCustomerPaymentMethodModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  paymentMethod?: iCustomerPaymentMethod;
  onSuccess?: () => void;
  retry: () => void;
}

export function AddEditCustomerPaymentMethodModal({
  open,
  onOpenChange,
  mode,
  paymentMethod,
  onSuccess,
  retry,
}: AddEditCustomerPaymentMethodModalProps) {
  const [label, setLabel] = useState(paymentMethod?.label ?? "");
  const [enablePDQCharges, setEnablePDQCharges] = useState(
    paymentMethod?.enablePDQCharges ?? false
  );
  const [PDQChargeFormula, setPDQChargeFormula] = useState(
    paymentMethod?.PDQChargeFormula ?? ""
  );
  const [enableCustomerSurcharge, setEnableCustomerSurcharge] = useState(
    paymentMethod?.enableCustomerSurcharge ?? false
  );
  const [customerSurchargeFormula, setCustomerSurchargeFormula] = useState(
    paymentMethod?.customerSurchargeFormula ?? ""
  );
  const [requirePDQReconcilation, setRequirePDQReconcilation] = useState(
    paymentMethod?.requirePDQReconcilation ?? false
  );
  const [connectedLedger, setConnectedLedger] = useState(
    paymentMethod?.connectedLedger ?? ""
  );
  const [connectedQuickBooksAccountId, setConnectedQuickBooksAccountId] =
    useState(paymentMethod?.connectedQuickBooksAccountId ?? "");
  const [connectedQuickBooksAccountName, setConnectedQuickBooksAccountName] =
    useState(paymentMethod?.connectedQuickBooksAccountName ?? "");
  const [connectedQuickBooksAccountType, setConnectedQuickBooksAccountType] =
    useState(paymentMethod?.connectedQuickBooksAccountType ?? "");
  const [archived, setArchived] = useState(paymentMethod?.archived ?? false);
  const [restrictByBrands, setRestrictByBrands] = useState(
    paymentMethod?.restrictByBrands ?? false
  );
  const [allowedBrands, setAllowedBrands] = useState<string[]>(
    paymentMethod?.allowedBrands ?? []
  );
  const { brands } = useBrands();
  const defaultBrandCurrency = useMemo(() => {
    const defaultBrand = brands.find((brand) => brand.isDefault) ?? brands[0];
    return defaultBrand?.homeCurrency?.trim().toUpperCase() ?? "GBP";
  }, [brands]);
  const [defaultCurrency, setDefaultCurrency] = useState(
    paymentMethod?.defaultCurrency?.trim().toUpperCase() ?? defaultBrandCurrency
  );

  const { connected: quickBooksConnected, accounts: quickBooksAccounts } =
    useQuickBooksAccounts(open);

  const [formulaCheck, setFormulaCheck] = useState<{
    status: "idle" | "checking" | "valid" | "invalid";
    message?: string;
  }>({ status: "idle" });
  const [surchargeFormulaCheck, setSurchargeFormulaCheck] = useState<{
    status: "idle" | "checking" | "valid" | "invalid";
    message?: string;
  }>({ status: "idle" });

  useEffect(() => {
    if (open) {
      setLabel(paymentMethod?.label ?? "");
      setEnablePDQCharges(paymentMethod?.enablePDQCharges ?? false);
      setPDQChargeFormula(paymentMethod?.PDQChargeFormula ?? "");
      setEnableCustomerSurcharge(paymentMethod?.enableCustomerSurcharge ?? false);
      setCustomerSurchargeFormula(paymentMethod?.customerSurchargeFormula ?? "");
      setRequirePDQReconcilation(paymentMethod?.requirePDQReconcilation ?? false);
      setConnectedLedger(paymentMethod?.connectedLedger ?? "");
      setConnectedQuickBooksAccountId(
        paymentMethod?.connectedQuickBooksAccountId ?? ""
      );
      setConnectedQuickBooksAccountName(
        paymentMethod?.connectedQuickBooksAccountName ?? ""
      );
      setConnectedQuickBooksAccountType(
        paymentMethod?.connectedQuickBooksAccountType ?? ""
      );
      setArchived(paymentMethod?.archived ?? false);
      setRestrictByBrands(paymentMethod?.restrictByBrands ?? false);
      setAllowedBrands(paymentMethod?.allowedBrands ?? []);
      setDefaultCurrency(
        paymentMethod?.defaultCurrency?.trim().toUpperCase() ??
          defaultBrandCurrency
      );
      setFormulaCheck({ status: "idle" });
      setSurchargeFormulaCheck({ status: "idle" });
    }
  }, [
    open,
    paymentMethod?.label,
    paymentMethod?.enablePDQCharges,
    paymentMethod?.PDQChargeFormula,
    paymentMethod?.enableCustomerSurcharge,
    paymentMethod?.customerSurchargeFormula,
    paymentMethod?.requirePDQReconcilation,
    paymentMethod?.connectedLedger,
    paymentMethod?.connectedQuickBooksAccountId,
    paymentMethod?.connectedQuickBooksAccountName,
    paymentMethod?.connectedQuickBooksAccountType,
    paymentMethod?.archived,
    paymentMethod?.restrictByBrands,
    paymentMethod?.allowedBrands,
    paymentMethod?.defaultCurrency,
    defaultBrandCurrency,
  ]);

  const { doAction: createPaymentMethod, isProcessing: isCreateProcessing } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (details: Partial<iCustomerPaymentMethod>) =>
          SDK.customerPaymentMethods.create(details),
        successMessage: "Payment method has been created",
        failureMessage: "Something went wrong",
      }),
      []
    );

  const { doAction: editPaymentMethod, isProcessing: isEditProcessing } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (
          id: string,
          edits: Partial<iCustomerPaymentMethod>
        ) => SDK.customerPaymentMethods.editById(id, edits),
        successMessage: "Payment method has been updated",
        failureMessage: "Something went wrong",
      }),
      []
    );

  const isProcessing = isCreateProcessing || isEditProcessing;
  const title =
    mode === "create" ? "Add payment method" : "Edit payment method";
  const connectedLedgerCurrency =
    mode === "create"
      ? defaultCurrency
      : paymentMethod?.defaultCurrency?.trim().toUpperCase() ?? defaultCurrency;

  const handleSubmit = useCallback(async () => {
    const trimmedLabel = label.trim();
    if (!trimmedLabel) return;

    const currency = defaultCurrency.trim().toUpperCase();
    if (mode === "create" && !currency) return;

    const quickBooksFields = {
      connectedQuickBooksAccountId:
        connectedQuickBooksAccountId?.trim() || undefined,
      connectedQuickBooksAccountName:
        connectedQuickBooksAccountId?.trim()
          ? connectedQuickBooksAccountName?.trim() || undefined
          : undefined,
      connectedQuickBooksAccountType:
        connectedQuickBooksAccountId?.trim()
          ? connectedQuickBooksAccountType?.trim() || undefined
          : undefined,
    };

    const brandRestrictionFields = {
      restrictByBrands,
      allowedBrands: restrictByBrands ? allowedBrands : undefined,
    };

    if (mode === "create") {
      const created = await createPaymentMethod({
        label: trimmedLabel,
        type: "DEFAULT",
        defaultCurrency: currency,
        enablePDQCharges,
        PDQChargeFormula: PDQChargeFormula?.trim() || undefined,
        enableCustomerSurcharge,
        customerSurchargeFormula: customerSurchargeFormula?.trim() || undefined,
        requirePDQReconcilation,
        connectedLedger:
          requirePDQReconcilation === false
            ? connectedLedger?.trim() || undefined
            : undefined,
        ...quickBooksFields,
        ...brandRestrictionFields,
      });
      if (created) {
        retry();
        onOpenChange(false);
        onSuccess?.();
      }
    } else if (paymentMethod?.id) {
      await editPaymentMethod(paymentMethod.id, {
        label: trimmedLabel,
        enablePDQCharges,
        PDQChargeFormula: PDQChargeFormula?.trim() || undefined,
        enableCustomerSurcharge,
        customerSurchargeFormula: customerSurchargeFormula?.trim() || undefined,
        requirePDQReconcilation,
        connectedLedger:
          requirePDQReconcilation === false
            ? connectedLedger?.trim() || undefined
            : undefined,
        ...quickBooksFields,
        ...brandRestrictionFields,
        archived,
      });
      retry();
      onOpenChange(false);
      onSuccess?.();
    }
  }, [
    label,
    defaultCurrency,
    mode,
    paymentMethod?.id,
    enablePDQCharges,
    PDQChargeFormula,
    enableCustomerSurcharge,
    customerSurchargeFormula,
    requirePDQReconcilation,
    connectedLedger,
    connectedQuickBooksAccountId,
    connectedQuickBooksAccountName,
    connectedQuickBooksAccountType,
    restrictByBrands,
    allowedBrands,
    archived,
    createPaymentMethod,
    editPaymentMethod,
    retry,
    onOpenChange,
    onSuccess,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <DialogHeader className="shrink-0 border-b px-6 py-4 text-left">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="customer-pm-label">Label</Label>
            <Input
              id="customer-pm-label"
              placeholder="e.g. Card, Bank transfer"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />
          </div>

          {mode === "create" ? (
            <CurrencySelector
              id="customer-pm-default-currency"
              label="Default Accounting Currency"
              value={defaultCurrency}
              onValueChange={setDefaultCurrency}
              extraCurrencies={[defaultBrandCurrency]}
            />
          ) : paymentMethod?.defaultCurrency ? (
            <div className="grid gap-2">
              <Label>Default Accounting Currency</Label>
              <Input
                value={paymentMethod.defaultCurrency}
                readOnly
                disabled
                className="bg-muted"
              />
            </div>
          ) : null}

          {mode === "create" ? (
            <p className="text-xs text-muted-foreground -mt-2">
              Receiving, PDQ, and clearing ledgers are created in this currency.
              It cannot be changed later.
            </p>
          ) : null}

          <div className="border rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-1">
                <Label className="text-sm">Enable PDQ charges</Label>
                <p className="text-xs text-muted-foreground">
                  When enabled, PDQ charges can be recorded as trade expenses on the booking.
                  For Stripe-linked payment methods, actual fees from Stripe payments are
                  recorded automatically when webhooks create receipts.
                </p>
              </div>
              <Switch checked={enablePDQCharges} onCheckedChange={setEnablePDQCharges} />
            </div>

            {enablePDQCharges && (
              <div className="grid gap-2">
                <Label>PDQ charge formula</Label>
                <Textarea
                  value={PDQChargeFormula}
                  onChange={(e) => {
                    setPDQChargeFormula(e.target.value);
                    setFormulaCheck({ status: "idle" });
                  }}
                  placeholder='Examples: "amount * 0.02" or "amount * 0.015 + 0.2"'
                  rows={3}
                />
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>
                    Allowed variable: <span className="font-mono">amount</span>
                  </div>
                  <div className="font-mono">
                    amount * 0.02
                    <br />
                    amount * 0.015 + 0.2
                    <br />
                    (amount * 0.019) + 0.1
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={formulaCheck.status === "checking" || !PDQChargeFormula.trim()}
                    onClick={async () => {
                      setFormulaCheck({ status: "checking" });
                      try {
                        const res =
                          await SDK.validatePDQFormula({
                            formula: PDQChargeFormula.trim(),
                            amount: 100,
                          });
                        if (res.valid) {
                          setFormulaCheck({
                            status: "valid",
                            message: `Valid. For amount=100 the result is ${res.value ?? "—"}.`,
                          });
                        } else {
                          setFormulaCheck({
                            status: "invalid",
                            message: res.error ?? "Invalid formula",
                          });
                        }
                      } catch (e: any) {
                        setFormulaCheck({
                          status: "invalid",
                          message: String(e?.message ?? e),
                        });
                      }
                    }}
                  >
                    {formulaCheck.status === "checking" ? "Checking…" : "Check formula"}
                  </Button>
                  {formulaCheck.status !== "idle" && formulaCheck.status !== "checking" && (
                    <span
                      className={cn(
                        "text-xs",
                        formulaCheck.status === "valid"
                          ? "text-emerald-600"
                          : "text-destructive"
                      )}
                    >
                      {formulaCheck.message}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-4 pt-2 border-t">
              <div className="space-y-1">
                <Label className="text-sm">Enable customer payment surcharge</Label>
                <p className="text-xs text-muted-foreground">
                  When enabled, entering a payment in Add Payment Receipt will automatically
                  pre-fill the customer surcharge based on this formula.
                </p>
              </div>
              <Switch
                checked={enableCustomerSurcharge}
                onCheckedChange={setEnableCustomerSurcharge}
              />
            </div>

            {enableCustomerSurcharge && (
              <div className="grid gap-2">
                <Label>Customer surcharge formula</Label>
                <Textarea
                  value={customerSurchargeFormula}
                  onChange={(e) => {
                    setCustomerSurchargeFormula(e.target.value);
                    setSurchargeFormulaCheck({ status: "idle" });
                  }}
                  placeholder='Examples: "amount * 0.01" or "(amount * 0.015) + 0.20"'
                  rows={3}
                />
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>
                    Allowed variable: <span className="font-mono">amount</span>
                  </div>
                  <div className="font-mono">
                    amount * 0.01
                    <br />
                    (amount * 0.015) + 0.20
                    <br />
                    (amount * 0.02) + 0.20
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={
                      surchargeFormulaCheck.status === "checking" ||
                      !customerSurchargeFormula.trim()
                    }
                    onClick={async () => {
                      setSurchargeFormulaCheck({ status: "checking" });
                      try {
                        const res = await SDK.validatePDQFormula({
                          formula: customerSurchargeFormula.trim(),
                          amount: 100,
                        });
                        if (res.valid) {
                          setSurchargeFormulaCheck({
                            status: "valid",
                            message: `Valid. For amount=100 the result is ${res.value ?? "—"}.`,
                          });
                        } else {
                          setSurchargeFormulaCheck({
                            status: "invalid",
                            message: res.error ?? "Invalid formula",
                          });
                        }
                      } catch (e: any) {
                        setSurchargeFormulaCheck({
                          status: "invalid",
                          message: String(e?.message ?? e),
                        });
                      }
                    }}
                  >
                    {surchargeFormulaCheck.status === "checking"
                      ? "Checking…"
                      : "Check formula"}
                  </Button>
                  {surchargeFormulaCheck.status !== "idle" &&
                    surchargeFormulaCheck.status !== "checking" && (
                      <span
                        className={cn(
                          "text-xs",
                          surchargeFormulaCheck.status === "valid"
                            ? "text-emerald-600"
                            : "text-destructive"
                        )}
                      >
                        {surchargeFormulaCheck.message}
                      </span>
                    )}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-4 pt-2 border-t">
              <div className="space-y-1">
                <Label className="text-sm">Require PDQ reconcilation</Label>
                <p className="text-xs text-muted-foreground">
                  When enabled, receipts using this payment method will appear in the PDQ Reconciliation page.
                </p>
              </div>
              <Switch
                checked={requirePDQReconcilation}
                onCheckedChange={(v) => {
                  setRequirePDQReconcilation(v);
                  if (v) setConnectedLedger("");
                }}
              />
            </div>

            {requirePDQReconcilation === false && (
              <div className="space-y-2">
                <LedgerSelector
                  label="Connected ledger account"
                  value={connectedLedger}
                  onValueChange={setConnectedLedger}
                  allowNone
                  paymentAccountLedgersOnly
                  currencyFilter={connectedLedgerCurrency}
                  placeholder="Select ledger account"
                />
                <p className="text-xs text-muted-foreground">
                  If reconciliation is off and a ledger is selected, receipts
                  will be auto-posted as ledger transactions. The ledger must be
                  in {connectedLedgerCurrency || "the payment method currency"}.
                </p>
              </div>
            )}
          </div>

          {quickBooksConnected ? (
            <div className="space-y-2 rounded-lg border p-3">
              <QuickBooksAccountSelector
                id="customer-pm-qb-account"
                label="QuickBooks deposit account"
                accounts={quickBooksAccounts}
                value={connectedQuickBooksAccountId}
                onValueChange={(accountId, account) => {
                  setConnectedQuickBooksAccountId(accountId);
                  setConnectedQuickBooksAccountName(
                    account?.Name ?? account?.name ?? ""
                  );
                  setConnectedQuickBooksAccountType(
                    account?.AccountType ?? account?.accountType ?? ""
                  );
                }}
                accountTypeFilter={["Bank", "Other Current Asset"]}
                allowNone
                noneLabel="No QuickBooks account"
                placeholder="Select deposit account"
              />
              <p className="text-xs text-muted-foreground">
                Required for QuickBooks payment sync. Customer receipts are deposited to this account.
                {connectedQuickBooksAccountId ? (
                  <>
                    {" "}
                    Selected:{" "}
                    <span className="font-medium">
                      {formatQuickBooksAccountLabel({
                        id: connectedQuickBooksAccountId,
                        name: connectedQuickBooksAccountName,
                        accountType: connectedQuickBooksAccountType,
                      })}
                    </span>
                  </>
                ) : null}
              </p>
            </div>
          ) : null}

          <PaymentMethodBrandRestrictionFields
            restrictByBrands={restrictByBrands}
            allowedBrands={allowedBrands}
            onRestrictByBrandsChange={setRestrictByBrands}
            onAllowedBrandsChange={setAllowedBrands}
          />

          {mode === "edit" && paymentMethod?.defaultLedgerAccounts ? (
            <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
              <Label className="text-sm">System accounting ledgers</Label>
              <p className="text-xs text-muted-foreground">
                Auto-provisioned when this payment method was created. Used for
                journal posting on customer receipts.
              </p>
              <CustomerPaymentMethodDefaultLedgerLinks
                defaultLedgerAccounts={paymentMethod.defaultLedgerAccounts}
              />
            </div>
          ) : null}

          {mode === "edit" ? (
            <div className="flex items-center gap-2 rounded-lg border p-3">
              <Checkbox
                id="customer-pm-archived"
                checked={archived}
                onCheckedChange={(checked) => setArchived(checked === true)}
              />
              <div className="space-y-1">
                <Label htmlFor="customer-pm-archived" className="text-sm">
                  Archived
                </Label>
                <p className="text-xs text-muted-foreground">
                  Archived methods are hidden from new receipts but remain on
                  existing records.
                </p>
              </div>
            </div>
          ) : null}
        </div>
        </div>
        <DialogFooter className="shrink-0 border-t px-6 py-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isProcessing || !label.trim() || (mode === "create" && !defaultCurrency.trim())}
          >
            {isProcessing ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
